/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { hh } from "@chi/chi";
import { ParamDictToProps } from "@utils/params";
import { WbaxRoot } from "@chi/wbax";
import type { WbaCtx } from "@/context/wbactx.tsx";
import {
  EffectChain,
  type EffectDef,
  type EffectSlot,
  LiveEffects,
  type LiveEffectType,
} from "@/synth/effects.tsx";

//#region slots
export interface FxSlot extends EffectSlot {
  opts: Record<string, unknown>;
  bypassed: boolean;
}

export function FxSlotDefaults(type: LiveEffectType): Record<string, unknown> {
  return ParamDictToProps((LiveEffects[type] as EffectDef).params);
}

export function FxSlotNextId(fx: FxSlot[], type: string) {
  let n = 1;
  while (fx.some((f) => f.id === `${type}-${n}`)) n++;
  return `${type}-${n}`;
}

export function FxSlotAdd(fx: FxSlot[], type: LiveEffectType, opts?: Record<string, unknown>) {
  return [...fx, {
    id: FxSlotNextId(fx, type),
    type,
    opts: { ...FxSlotDefaults(type), ...opts },
    bypassed: false,
  }];
}

export function FxSlotRemove(fx: FxSlot[], slot: string) {
  return fx.filter((f) => f.id !== slot);
}

export function FxSlotMove(fx: FxSlot[], slot: string, by: number) {
  const from = fx.findIndex((f) => f.id === slot);
  const to = Math.min(fx.length - 1, Math.max(0, from + by));
  if (from < 0 || from === to) return fx;
  const next = [...fx];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function FxSlotMap(fx: FxSlot[], slot: string, fn: (slot: FxSlot) => FxSlot) {
  return fx.map((f) => (f.id === slot ? fn(f) : f));
}

export function FxSlotBypass(fx: FxSlot[], slot: string) {
  return FxSlotMap(fx, slot, (f) => ({ ...f, bypassed: !f.bypassed }));
}

export function FxSlotOpts(fx: FxSlot[], slot: string, opts: Record<string, unknown>) {
  return FxSlotMap(fx, slot, (f) => ({ ...f, opts: { ...f.opts, ...opts } }));
}
//#endregion

//#region loader
export interface FxLoaderOptions {
  wbactx: WbaCtx;
  base?: string;
}

function ErrorMessage(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

export class FxLoader {
  readonly wbactx: WbaCtx;
  readonly base: string;
  readonly changed$: rxjs.Observable<void>;
  readonly error$ = new rxjs.Subject<string>();

  #assets = new Map<string, AudioBuffer | "loading" | "error">();
  #loaded$ = new rxjs.BehaviorSubject<void>(undefined);

  constructor(options: FxLoaderOptions) {
    this.wbactx = options.wbactx;
    this.base = options.base ?? "";
    this.changed$ = rxjs.combineLatest([
      this.wbactx.select$((s) => s.worklets),
      this.#loaded$,
    ]).pipe(rxjs.map(() => undefined));
  }

  ready(slot: FxSlot): boolean {
    const def = LiveEffects[slot.type] as EffectDef;
    const worklets = this.wbactx.state.worklets;
    const pending = (def.worklets ?? []).filter((url) => worklets[this.base + url] !== "loaded");
    for (const url of pending) {
      if (worklets[this.base + url] !== undefined) continue;
      this.wbactx.addWorklet(this.base + url).catch((e) =>
        this.error$.next(`${slot.type}: ${this.base + url}: ${ErrorMessage(e)}`)
      );
    }
    if (def.assets) {
      const path = String(slot.opts.impulsePath ?? Object.keys(def.assets)[0]);
      if (!this.#assets.has(path)) this.#load(path, this.base + (def.assets[path] ?? path));
    }
    return pending.length === 0;
  }

  assets(): Record<string, AudioBuffer> {
    const ready: Record<string, AudioBuffer> = {};
    for (const [path, value] of this.#assets) {
      if (typeof value === "object") ready[path] = value;
    }
    return ready;
  }

  dispose() {
    this.#loaded$.complete();
    this.error$.complete();
  }

  #load(path: string, url: string) {
    this.#assets.set(path, "loading");
    const ctx = this.wbactx.ctx;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.arrayBuffer();
      })
      .then((data) => ctx.decodeAudioData(data))
      .then(
        (buffer) => {
          this.#assets.set(path, buffer);
          if (!this.#loaded$.closed) this.#loaded$.next();
        },
        (e) => {
          this.#assets.set(path, "error");
          if (!this.error$.closed) this.error$.next(`${url}: ${ErrorMessage(e)}`);
        },
      );
  }
}
//#endregion

//#region chain
export interface FxChainOptions {
  loader: FxLoader;
  input: AudioNode;
  output: AudioNode;
  path?: string;
}

export class FxChain {
  readonly loader: FxLoader;
  readonly input: AudioNode;
  readonly root: WbaxRoot;

  constructor(options: FxChainOptions) {
    this.loader = options.loader;
    this.input = options.input;
    const { wbactx } = this.loader;
    const path = options.path;
    this.root = new WbaxRoot({
      ctx: wbactx.ctx as unknown as BaseAudioContext,
      destination: options.output,
      create: path
        ? (make, info) => wbactx.node(() => make(), { id: `${path}${info.path}`, name: info.tag })
        : undefined,
    });
  }

  render(fx: FxSlot[]) {
    if (this.root.disposed) return;
    const slots = fx.filter((slot) => !slot.bypassed && this.loader.ready(slot));
    try {
      this.root.render(
        hh(
          EffectChain,
          { slots, assets: this.loader.assets() },
          hh("external", { node: this.input }),
        ),
      );
    } catch (e) {
      this.loader.error$.next(ErrorMessage(e));
    }
  }

  dispose() {
    this.root.dispose();
  }
}
//#endregion
